import { useState } from 'react';
import { OpsMark } from './ui.tsx';
import { AlertTriangle, ArrowRight, Info, X } from 'lucide-react';
import type { Briefing, Person } from '../api.ts';
import { useSave, useTaskDrawer } from '../data.ts';
import { ago, firstName } from '../util.ts';

// The ambient briefing: what changed for you and why, until you dismiss it.
export function BriefingBanner({ briefings, people }: { briefings: Briefing[]; people: Person[] }) {
  const save = useSave();
  const { open } = useTaskDrawer();
  const [expanded, setExpanded] = useState<string | null>(null);
  if (!briefings.length) return null;
  return (
    <div className="banners" role="region" aria-label="Briefings">
      {briefings.slice(0, 3).map((b) => {
        const Icon = b.priority === 'high' ? AlertTriangle : b.created_by === 'operator@agents.gbsoft.dev' ? OpsMark : Info;
        return (
          <div key={b.id} className={`banner p-${b.priority}`}>
            <Icon size={16} className="banner-icon" aria-hidden />
            <div className="banner-body">
              <p className="banner-text">{b.summary_text}</p>
              <p className="banner-meta">
                {b.created_by === 'operator@agents.gbsoft.dev' ? 'Ops' : firstName(b.created_by, people)}
                {b.on_behalf_of && ` · from ${firstName(b.on_behalf_of, people)}'s ${b.source_type}`} · {ago(b.created_at)}
              </p>
              {expanded === b.id && (
                <div className="banner-detail">
                  {b.detail && <p>{b.detail}</p>}
                  {b.changes?.length > 0 && (
                    <ul>{b.changes.map((c, i) => (
                      <li key={i}><button className="link" onClick={() => open(c.task_id)}>{c.title}</button>
                        {c.to === 'created' ? <span className="muted"> · new</span> : <span className="muted"> · {c.from ?? 'no date'} <ArrowRight size={11} aria-hidden /> {c.to}</span>}</li>
                    ))}</ul>
                  )}
                </div>
              )}
            </div>
            <div className="banner-actions">
              {(b.changes?.length > 0 || b.detail) && <button className="btn sm" onClick={() => setExpanded(expanded === b.id ? null : b.id)}>{expanded === b.id ? 'Hide' : 'View changes'}</button>}
              <button className="btn sm ghost" aria-label="Dismiss" onClick={() => save.mutate({ path: `/briefings/${b.id}/ack` })}><X size={14} aria-hidden /> Dismiss</button>
            </div>
          </div>
        );
      })}
      {briefings.length > 3 && <p className="muted sm">{briefings.length - 3} more briefings below the fold in Notes.</p>}
    </div>
  );
}
